import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, 
  faTimes, 
  faUserTie,
  faCalendarAlt,
  faClock,
  faBuilding,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { doc, updateDoc, collection, getDocs, query, where, addDoc, deleteDoc, limit, orderBy, getDoc, increment } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { createNotification, NOTIFICATION_TYPES } from '../../utils/NotificationService';
import styles from './ManagedBySection.module.css';
import SearchManagersModal from './SearchManagersModal';
import defaultProfilePic from '../../assets/Images/defaultUserProfilePicture.png';
import UserContext from '../../context/UserContext';

const ManagedBySection = ({ 
  managedBy = [], 
  pastManagedBy = [],
  onManagedByAdd,
  onManagedByRemove,
  onPastManagedByAdd,
  onPastManagedByRemove,
  userData,
  showAlert
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('current');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [isPastSearch, setIsPastSearch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // For displaying managers
  const [displayedCurrentManagers, setDisplayedCurrentManagers] = useState([]);
  const [displayedPastManagers, setDisplayedPastManagers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [hasMoreCurrent, setHasMoreCurrent] = useState(false);
  const [hasMorePast, setHasMorePast] = useState(false);
  
  // Get relevant functions from context
  const { setUserData } = useContext(UserContext);

  // Check if user type should show this section
  const shouldShowManagedBy = () => {
    if (!userData) return false;
    return userData.userType === 'Artist';
  };

  // Load initial data including pending requests
  useEffect(() => {
    if (userData?.userId) {
      // Set displayed managers from the props first
      setDisplayedCurrentManagers(managedBy || []);
      setDisplayedPastManagers(pastManagedBy || []);
      
      // Check if there are more managers using counter fields
      setHasMoreCurrent((userData.managedByCount || 0) > (managedBy?.length || 0));
      setHasMorePast((userData.pastManagedByCount || 0) > (pastManagedBy?.length || 0));
      
      // Fetch pending association requests
      const loadPendingRequests = async () => {
        setIsLoading(true);
        try {
          const db = getFirestoreInstance();
          const pendingRequestsRef = collection(db, 'users', userData.userId, 'pendingManagedBy');
          const requestsSnapshot = await getDocs(pendingRequestsRef);
          
          if (!requestsSnapshot.empty) {
            const currentRequests = [];
            const pastRequests = [];
            
            requestsSnapshot.docs.forEach(doc => {
              const pendingData = doc.data();
              
              // Create the manager object with the proper structure for UI display
              const request = {
                id: doc.id,
                userId: pendingData.userId,
                status: pendingData.status || 'pending',
                type: pendingData.type || 'manager',
                isPending: true, // Mark as pending for UI
                docRef: doc.ref, // Store reference for easy deletion
                createdAt: pendingData.createdAt || new Date().toISOString(),
                isPast: pendingData.isPast || false,
                
                // IMPORTANT: Map the target organization's display data to the top level
                // This is what the UI expects - check both targetData and direct fields
                fullName: pendingData.targetData?.fullName || '',
                userName: pendingData.targetData?.userName || '',
                profilePictureUrl: pendingData.targetData?.profilePictureUrl || '',
                userSubType: pendingData.targetData?.userSubType || '',
                idVerificationStatus: pendingData.targetData?.idVerificationStatus || '',
                
                // Add the relevant date fields based on whether it's past or current
                ...(pendingData.isPast 
                  ? { duration: pendingData.duration } 
                  : { since: pendingData.since })
              };
              
              // Sort into current or past based on isPast flag
              if (pendingData.isPast) {
                pastRequests.push(request);
              } else {
                currentRequests.push(request);
              }
            });
            
            // Add to displayed current managers with isPending flag
            setDisplayedCurrentManagers(prev => {
              // Create a new array with pending requests that don't already exist
              const existingIds = new Set(prev.map(manager => manager.id));
              const newPendingManagers = currentRequests.filter(req => !existingIds.has(req.id));
              
              // Combine existing managers with new pending managers
              return [...newPendingManagers, ...prev];
            });
            
            // Add to displayed past managers with isPending flag
            setDisplayedPastManagers(prev => {
              // Create a new array with pending requests that don't already exist
              const existingIds = new Set(prev.map(manager => manager.id));
              const newPendingManagers = pastRequests.filter(req => !existingIds.has(req.id));
              
              // Combine existing managers with new pending managers
              return [...newPendingManagers, ...prev];
            });
            
            // Also store separately if needed
            setPendingRequests([...currentRequests, ...pastRequests]);
          }
        } catch (error) {
          console.error('Error fetching pending requests:', error);
          showAlert({
            title: 'Error',
            message: 'Failed to load pending manager requests.',
            type: 'error'
          });
        } finally {
          setIsLoading(false);
        }
      };
      
      loadPendingRequests();
    }
  }, [userData, managedBy, pastManagedBy]);
  
  
  // Function to fetch pending manager requests
  const fetchPendingRequests = async () => {
    if (!userData?.userId) return;
    
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      const pendingRequestsRef = collection(db, 'users', userData.userId, 'pendingManagedBy');
      const requestsSnapshot = await getDocs(pendingRequestsRef);
      
      if (!requestsSnapshot.empty) {
        const currentRequests = [];
        const pastRequests = [];
        
        requestsSnapshot.docs.forEach(doc => {
          const pendingData = doc.data();
          
          // Create the manager object with the proper structure for UI display
          const request = {
            id: doc.id,
            userId: pendingData.userId,
            status: pendingData.status || 'pending',
            type: pendingData.type || 'manager',
            isPending: true, // Mark as pending for UI
            docRef: doc.ref, // Store reference for easy deletion
            createdAt: pendingData.createdAt || new Date().toISOString(),
            isPast: pendingData.isPast || false,
            
            // IMPORTANT: Map the target organization's display data to the top level
            // This is what the UI expects - check first if targetData exists
            fullName: pendingData.targetData?.fullName || '',
            userName: pendingData.targetData?.userName || '',
            profilePictureUrl: pendingData.targetData?.profilePictureUrl || '',
            userSubType: pendingData.targetData?.userSubType || '',
            idVerificationStatus: pendingData.targetData?.idVerificationStatus || '',
            
            // Add the relevant date fields based on whether it's past or current
            ...(pendingData.isPast 
              ? { duration: pendingData.duration } 
              : { since: pendingData.since })
          };
          
          // Sort into current or past based on isPast flag
          if (request.isPast) {
            pastRequests.push(request);
          } else {
            currentRequests.push(request);
          }
        });
        
        // Add to displayed current managers with isPending flag
        setDisplayedCurrentManagers(prev => {
          // Create a new array with pending requests that don't already exist
          const existingIds = new Set(prev.map(manager => manager.id));
          const newPendingManagers = currentRequests.filter(req => !existingIds.has(req.id));
          
          // Combine existing managers with new pending managers
          return [...newPendingManagers, ...prev];
        });
        
        // Add to displayed past managers with isPending flag
        setDisplayedPastManagers(prev => {
          // Create a new array with pending requests that don't already exist
          const existingIds = new Set(prev.map(manager => manager.id));
          const newPendingManagers = pastRequests.filter(req => !existingIds.has(req.id));
          
          // Combine existing managers with new pending managers
          return [...newPendingManagers, ...prev];
        });
        
        // Also store separately if needed
        setPendingRequests([...currentRequests, ...pastRequests]);
      }
    } catch (error) {
      console.error('Error fetching pending requests:', error);
      showAlert({
        title: 'Error',
        message: 'Failed to load pending manager requests.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load more managers from subcollection
  const loadMoreManagers = async (isPast = false) => {
    if (loadingMore || !userData?.userId) return;
    
    setLoadingMore(true);
    try {
      const db = getFirestoreInstance();
      const managersRef = collection(db, 'users', userData.userId, 'managedBy');
      
      // Get the currently displayed managers
      const currentDisplayed = isPast ? displayedPastManagers : displayedCurrentManagers;
      
      // Skip already displayed items
      const managersQuery = query(
        managersRef,
        where('isPast', '==', isPast),
        orderBy('createdAt', 'desc'),
        limit(3) // Load next 3 managers
      );
      
      const managersSnapshot = await getDocs(managersQuery);
      
      if (!managersSnapshot.empty) {
        const allManagers = managersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter out managers that are already displayed
        const displayedIds = currentDisplayed.map(manager => manager.id);
        const newManagers = allManagers.filter(manager => !displayedIds.includes(manager.id));
        
        // Update the displayed managers
        if (isPast) {
          const updatedPastManagers = [...currentDisplayed, ...newManagers];
          setDisplayedPastManagers(updatedPastManagers);
          // Update the "hasMore" flag based on counter
          setHasMorePast((userData.pastManagedByCount || 0) > updatedPastManagers.length);
        } else {
          // For current managers, preserve the pending requests at the top
          const pendingManagers = currentDisplayed.filter(manager => manager.isPending);
          const confirmedManagers = currentDisplayed.filter(manager => !manager.isPending);
          
          const updatedCurrentManagers = [...pendingManagers, ...confirmedManagers, ...newManagers];
          setDisplayedCurrentManagers(updatedCurrentManagers);
          // Update the "hasMore" flag based on counter
          setHasMoreCurrent((userData.managedByCount || 0) > (updatedCurrentManagers.length - pendingManagers.length));
        }
      } else {
        // If no more managers are found, set hasMore to false
        if (isPast) {
          setHasMorePast(false);
        } else {
          setHasMoreCurrent(false);
        }
      }
    } catch (error) {
      console.error(`Error loading more ${isPast ? 'past' : 'current'} managers:`, error);
      showAlert({
        title: 'Error',
        message: `Failed to load more ${isPast ? 'past' : 'current'} managers.`,
        type: 'error'
      });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSearchSelection = async (selected) => {
    try {
      setIsLoading(true);
      
      // For past associations, check required fields
      if (isPastSearch) {
        await addPastManager(selected);
      } else {
        await addCurrentManager(selected);
      }
    } catch (error) {
      console.error('Error adding manager:', error);
      showAlert({
        title: 'Error',
        message: 'Failed to add manager. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
      setShowSearchModal(false);
      setIsPastSearch(false);
    }
  };

  const addCurrentManager = async (selected) => {
    const db = getFirestoreInstance();
    
    try {
      setIsLoading(true);
      
      // Format date information for notification message
      const formattedSince = `Since: ${selected.since.month}/${selected.since.year}`;
      
      // Determine entity type label for message
      const entityTypeLabel = selected.type === 'organization' ? 'managing Organization' : 'manager';
      
      // CRITICAL: Get the exact notification type constant from NotificationService.js
      const NOTIFICATION_TYPE = NOTIFICATION_TYPES.MANAGER_REQUEST;
      
      // Create notification data first - before creating the document
      const notificationData = {
        type: NOTIFICATION_TYPE,
        title: `${entityTypeLabel.charAt(0).toUpperCase() + entityTypeLabel.slice(1)} Request`,
        message: `${userData.fullName} wants to add you as their current ${entityTypeLabel}. ${formattedSince}`,
        organizationId: selected.type === 'organization' ? selected.id : null,
        userId: selected.id, // This is the recipient's ID
        artistId: userData.userId, // Artist's ID who is making the request
        status: 'pending',
        timestamp: new Date().toISOString(),
        // These fields are needed by the notification system
        fullName: selected.fullName,
        userName: selected.userName,
        profilePictureUrl: selected.profilePictureUrl || '',
        createdBy: {
          userId: userData.userId,
          fullName: userData.fullName,
          userName: userData.userName,
          profilePictureUrl: userData.profilePictureUrl || ''
        },
        data: {
          _displayData: {
            artistName: userData.fullName,
            artistUserName: userData.userName,
            artistProfilePicture: userData.profilePictureUrl || '',
            managerName: selected.fullName,
            managerUserName: selected.userName,
            managerType: selected.type,
            sinceMonth: selected.since.month,
            sinceYear: selected.since.year,
            isPast: false
          }
        }
      };
      
      // Create notification first to get the document ID
      const notificationDocRef = await addDoc(
        collection(db, 'users', selected.id, 'notifications'), 
        notificationData
      );
      
      // Get the notification ID
      const notificationDocId = notificationDocRef.id;
      
      // Now create the manager request data with notification ID reference
      const managerRequest = {
        userId: selected.id,
        type: selected.type || 'manager',
        status: 'pending',
        isPast: false,
        createdAt: new Date().toISOString(),
        requestedAt: new Date().toISOString(),
        since: selected.since,
        // Store the notification document ID for direct access later
        notificationDocId: notificationDocId,
        targetData: {
          fullName: selected.fullName || '',
          userName: selected.userName || '',
          profilePictureUrl: selected.profilePictureUrl || '',
          userSubType: selected.userSubType || '',
          idVerificationStatus: selected.idVerificationStatus || ''
        },
        requestedBy: {
          userId: userData.userId,
          userName: userData.userName,
          fullName: userData.fullName,
          profilePictureUrl: userData.profilePictureUrl || '',
          userType: userData.userType
        }
      };
      
      // Add to user's pendingManagedBy subcollection
      const pendingRequestsRef = collection(db, 'users', userData.userId, 'pendingManagedBy');
      const newRequestDoc = await addDoc(pendingRequestsRef, managerRequest);
      
      // Update the notification with the request ID
      await updateDoc(notificationDocRef, {
        requestId: newRequestDoc.id,
        data: {
          ...notificationData.data,
          requestData: {
            requestDocId: newRequestDoc.id,
            since: selected.since
          }
        }
      });
      
      // For UI display, create a properly structured object
      const pendingManager = {
        id: newRequestDoc.id,
        userId: selected.id,
        fullName: selected.fullName,
        userName: selected.userName,
        profilePictureUrl: selected.profilePictureUrl || '',
        userSubType: selected.userSubType || '',
        idVerificationStatus: selected.idVerificationStatus || '',
        status: 'pending',
        isPending: true,
        since: selected.since,
        type: selected.type || 'manager',
        createdAt: new Date().toISOString(),
        notificationDocId: notificationDocId // Store notification ID for direct access
      };
      
      // Update displayed current managers with the new pending request at the top
      setDisplayedCurrentManagers(prev => [pendingManager, ...prev]);
      
      // Also update the separate pending requests array
      setPendingRequests(prev => [pendingManager, ...prev]);
      
      showAlert({
        title: 'Request Sent',
        message: 'Manager request sent successfully. Waiting for approval.',
        type: 'success'
      });
    } catch (error) {
      console.error('Error sending manager request:', error);
      showAlert({
        title: 'Error',
        message: 'Failed to send manager request. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Updated addPastManager function with notification ID tracking
  const addPastManager = async (selected) => {
    const db = getFirestoreInstance();
    
    try {
      setIsLoading(true);
      
      // Format date information for notification message
      const formattedDuration = `From: ${selected.duration.startMonth}/${selected.duration.startYear} To: ${selected.duration.endMonth}/${selected.duration.endYear}`;
      
      // Determine entity type label for message
      const entityTypeLabel = selected.type === 'organization' ? 'managing Organization' : 'manager';
      
      // CRITICAL: Get the exact notification type constant from NotificationService.js
      const NOTIFICATION_TYPE = NOTIFICATION_TYPES.PAST_MANAGER_REQUEST;
      
      // Create notification data first - before creating the document
      const notificationData = {
        type: NOTIFICATION_TYPE,
        title: `Past ${entityTypeLabel.charAt(0).toUpperCase() + entityTypeLabel.slice(1)} Request`,
        message: `${userData.fullName} wants to add you as their past ${entityTypeLabel}. ${formattedDuration}`,
        organizationId: selected.type === 'organization' ? selected.id : null,
        userId: selected.id, // This is the recipient's ID
        artistId: userData.userId, // Artist's ID who is making the request
        isPast: true,
        status: 'pending',
        timestamp: new Date().toISOString(),
        // These fields are needed by the notification system
        fullName: selected.fullName,
        userName: selected.userName,
        profilePictureUrl: selected.profilePictureUrl || '',
        createdBy: {
          userId: userData.userId,
          fullName: userData.fullName,
          userName: userData.userName,
          profilePictureUrl: userData.profilePictureUrl || ''
        },
        data: {
          _displayData: {
            artistName: userData.fullName,
            artistUserName: userData.userName,
            artistProfilePicture: userData.profilePictureUrl || '',
            managerName: selected.fullName,
            managerUserName: selected.userName,
            managerType: selected.type,
            startMonth: selected.duration.startMonth,
            startYear: selected.duration.startYear,
            endMonth: selected.duration.endMonth,
            endYear: selected.duration.endYear,
            isPast: true
          }
        }
      };
      
      // Create notification first to get the document ID
      const notificationDocRef = await addDoc(
        collection(db, 'users', selected.id, 'notifications'), 
        notificationData
      );
      
      // Get the notification ID
      const notificationDocId = notificationDocRef.id;
      
      // Now create the manager request data with notification ID reference
      const managerRequest = {
        userId: selected.id,
        type: selected.type || 'manager',
        status: 'pending',
        isPast: true,
        createdAt: new Date().toISOString(),
        requestedAt: new Date().toISOString(),
        duration: selected.duration,
        // Store the notification document ID for direct access later
        notificationDocId: notificationDocId,
        targetData: {
          fullName: selected.fullName || '',
          userName: selected.userName || '',
          profilePictureUrl: selected.profilePictureUrl || '',
          userSubType: selected.userSubType || '',
          idVerificationStatus: selected.idVerificationStatus || ''
        },
        requestedBy: {
          userId: userData.userId,
          userName: userData.userName,
          fullName: userData.fullName,
          profilePictureUrl: userData.profilePictureUrl || '',
          userType: userData.userType
        }
      };
      
      // Add to user's pendingManagedBy subcollection
      const pendingRequestsRef = collection(db, 'users', userData.userId, 'pendingManagedBy');
      const newRequestDoc = await addDoc(pendingRequestsRef, managerRequest);
      
      // Update the notification with the request ID
      await updateDoc(notificationDocRef, {
        requestId: newRequestDoc.id,
        data: {
          ...notificationData.data,
          requestData: {
            requestDocId: newRequestDoc.id,
            duration: selected.duration
          }
        }
      });
      
      // For UI display, create a properly structured object
      const pendingManager = {
        id: newRequestDoc.id,
        userId: selected.id,
        fullName: selected.fullName,
        userName: selected.userName,
        profilePictureUrl: selected.profilePictureUrl || '',
        userSubType: selected.userSubType || '',
        idVerificationStatus: selected.idVerificationStatus || '',
        status: 'pending',
        isPending: true,
        duration: selected.duration,
        type: selected.type || 'manager',
        isPast: true,
        createdAt: new Date().toISOString(),
        notificationDocId: notificationDocId // Store notification ID for direct access
      };
      
      // Update displayed past managers with the new pending request at the top
      setDisplayedPastManagers(prev => [pendingManager, ...prev]);
      
      // Also update the separate pending requests array
      setPendingRequests(prev => [pendingManager, ...prev]);
      
      showAlert({
        title: 'Request Sent',
        message: 'Past manager request sent successfully. Waiting for approval.',
        type: 'success'
      });
    } catch (error) {
      console.error('Error sending past manager request:', error);
      showAlert({
        title: 'Error',
        message: 'Failed to send past manager request. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  

  const handleRemoveManager = async (manager, isPast = false) => {
    try {
      setIsLoading(true);
      
      // If this is a pending request, handle differently
      if (manager.isPending) {
        await handleCancelPendingRequest(manager);
        return;
      }
      
      const db = getFirestoreInstance();
      const userRef = doc(db, 'users', userData.userId);
      const managersRef = collection(db, 'users', userData.userId, 'managedBy');
      
      // Find the document in the subcollection by ID
      await deleteDoc(doc(managersRef, manager.id));
      
      // Update the main document arrays with new top 3
      if (isPast) {
        // Get new top 3 past managers
        const pastManagersQuery = query(
          managersRef,
          where('isPast', '==', true),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        
        const pastManagersSnapshot = await getDocs(pastManagersQuery);
        const top3PastManagers = await Promise.all(
          pastManagersSnapshot.docs.map(async (docSnapshot) => {
            const managerData = docSnapshot.data();
            
            // Enrich with user data
            try {
              const managerDoc = await getDoc(doc(db, 'users', managerData.userId));
              if (managerDoc.exists()) {
                const userData = managerDoc.data();
                return {
                  id: docSnapshot.id,
                  ...managerData,
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  idVerificationStatus: userData.idVerificationStatus || ''
                };
              }
            } catch (error) {
              console.error(`Error fetching manager data for ${managerData.userId}:`, error);
            }
            
            // Return the manager data as is if enrichment failed
            return {
              id: docSnapshot.id,
              ...managerData
            };
          })
        );
        
        // Update main document and decrement the counter
        await updateDoc(userRef, {
          pastManagedBy: top3PastManagers,
          pastManagedByCount: increment(-1) // Decrement the counter
        });
        
        // Update displayed managers
        setDisplayedPastManagers(prev => prev.filter(m => m.id !== manager.id));
        
        // Update hasMore flag based on the new count
        setHasMorePast((userData.pastManagedByCount || 0) - 1 > top3PastManagers.length);
        
        // Update the user data in context
        setUserData(prev => ({
          ...prev,
          pastManagedBy: top3PastManagers,
          pastManagedByCount: Math.max((prev.pastManagedByCount || 0) - 1, 0)
        }));
        
        // Notify parent components
        if (onPastManagedByRemove) {
          onPastManagedByRemove(manager.id);
        }
      } else {
        // Get new top 3 current managers
        const currentManagersQuery = query(
          managersRef,
          where('isPast', '==', false),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        
        const currentManagersSnapshot = await getDocs(currentManagersQuery);
        const top3CurrentManagers = await Promise.all(
          currentManagersSnapshot.docs.map(async (docSnapshot) => {
            const managerData = docSnapshot.data();
            
            // Enrich with user data
            try {
              const managerDoc = await getDoc(doc(db, 'users', managerData.userId));
              if (managerDoc.exists()) {
                const userData = managerDoc.data();
                return {
                  id: docSnapshot.id,
                  ...managerData,
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  idVerificationStatus: userData.idVerificationStatus || ''
                };
              }
            } catch (error) {
              console.error(`Error fetching manager data for ${managerData.userId}:`, error);
            }
            
            // Return the manager data as is if enrichment failed
            return {
              id: docSnapshot.id,
              ...managerData
            };
          })
        );
        
        // Update main document and decrement the counter
        await updateDoc(userRef, {
          managedBy: top3CurrentManagers,
          managedByCount: increment(-1) // Decrement the counter
        });
        
        // Update displayed managers
        setDisplayedCurrentManagers(prev => prev.filter(m => m.id !== manager.id));
        
        // Update hasMore flag based on the new count
        setHasMoreCurrent((userData.managedByCount || 0) - 1 > top3CurrentManagers.length);
        
        // Update the user data in context
        setUserData(prev => ({
          ...prev,
          managedBy: top3CurrentManagers,
          managedByCount: Math.max((prev.managedByCount || 0) - 1, 0)
        }));
        
        // Notify parent components
        if (onManagedByRemove) {
          onManagedByRemove(manager.id);
        }
      }
      
      // Now update the manager's side of the relationship if it was accepted
      if (manager.status === 'accepted') {
        try {
          // Find the manager's managingActs entry for this artist
          const managingActsRef = collection(db, 'users', manager.userId, 'managingActs');
          const managingActsQuery = query(
            managingActsRef,
            where('userId', '==', userData.userId)
          );
          
          const managingActsSnapshot = await getDocs(managingActsQuery);
          
          if (!managingActsSnapshot.empty) {
            // Delete from manager's managingActs
            for (const doc of managingActsSnapshot.docs) {
              await deleteDoc(doc.ref);
            }
            
            // Update the manager's main document with top 3
            const managerUserRef = doc(db, 'users', manager.userId);
            const managerDoc = await getDoc(managerUserRef);
            
            if (managerDoc.exists()) {
              // Get new top 3 managingActs for main document
              const managingActsTopQuery = query(
                managingActsRef,
                where('isPast', '==', isPast),
                orderBy('createdAt', 'desc'),
                limit(3)
              );
              
              const topActsSnapshot = await getDocs(managingActsTopQuery);
              const top3Acts = topActsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }));
              
              // Update the manager's main document
              if (isPast) {
                await updateDoc(managerUserRef, {
                  pastManagingActs: top3Acts,
                  pastManagingActsCount: increment(-1)
                });
              } else {
                await updateDoc(managerUserRef, {
                  managingActs: top3Acts,
                  managingActsCount: increment(-1)
                });
              }
            }
          }
          
          // Create notification for the manager
          await createNotification(manager.userId, 'general', {
            type: NOTIFICATION_TYPES.MANAGER_REMOVAL,
            title: 'Manager Relationship Removed',
            message: `${userData.fullName} has removed you as their ${isPast ? 'past ' : ''}manager.`,
            userId: userData.userId,
            status: 'removed',
            timestamp: new Date().toISOString(),
            data: {
              _displayData: {
                artistName: userData.fullName,
                artistId: userData.userId
              }
            }
          });
        } catch (error) {
          console.error('Error updating manager records:', error);
          // Continue even if this part fails
        }
      }
      
      showAlert({
        title: 'Success',
        message: `${isPast ? 'Past m' : 'M'}anager removed successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error removing manager:', error);
      showAlert({
        title: 'Error',
        message: 'Failed to remove manager. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle canceling a pending manager request
  const handleCancelPendingRequest = async (manager) => {
    try {
      const db = getFirestoreInstance();
      
      // 1. Delete from pendingManagedBy collection
      if (manager.docRef) {
        await deleteDoc(manager.docRef);
      } else if (manager.id) {
        await deleteDoc(doc(db, 'users', userData.userId, 'pendingManagedBy', manager.id));
      }
      
      // 2. Remove from UI - filter by ID
      if (manager.isPast) {
        setDisplayedPastManagers(prev => prev.filter(m => m.id !== manager.id));
      } else {
        setDisplayedCurrentManagers(prev => prev.filter(m => m.id !== manager.id));
      }
      setPendingRequests(prev => prev.filter(m => m.id !== manager.id));
      
      // 3. Delete the notification directly using stored ID
      try {
        // Direct deletion using stored notification ID - clean and efficient
        if (!manager.notificationDocId) {
          throw new Error('Notification document ID not found in manager data');
        }
        
        await deleteDoc(doc(db, 'users', manager.userId, 'notifications', manager.notificationDocId));
        console.log('Notification deleted directly using stored ID');
      } catch (notifError) {
        console.error('Error removing notification:', notifError);
        // Continue even if notification removal fails - don't block the process
      }
      
      showAlert({
        title: 'Success',
        message: 'Pending request canceled successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error canceling pending request:', error);
      showAlert({
        title: 'Error',
        message: 'Failed to cancel request. Please try again.',
        type: 'error'
      });
      throw error;
    }
  };

  // Open search modal for adding a current or past manager
  const openSearchModal = (isPast = false) => {
    setIsPastSearch(isPast);
    setShowSearchModal(true);
  };

  // Format duration for display
  const formatDuration = (duration) => {
    if (!duration) return '';
    
    const { startMonth, startYear, endMonth, endYear } = duration;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    return `${months[parseInt(startMonth) - 1]} ${startYear} - ${months[parseInt(endMonth) - 1]} ${endYear}`;
  };
  
  // Format since date for display
  const formatSince = (since) => {
    if (!since) return '';
    
    const { month, year } = since;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    return `Since ${months[parseInt(month) - 1]} ${year}`;
  };

  const navigateToManagerProfile = (userName) => {
    if (userName) {
      navigate(`/${userName}`);
    }
  };

  // If this user type shouldn't show managed by section, return null
  if (!shouldShowManagedBy()) {
    return null;
  }

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Managed By</h2>
      <div className={styles.sectionDescription}>
        Add managers or organizations that represent you professionally.
      </div>
      
      {/* Tabs for Current/Past */}
      <div className={styles.managersTabs}>
        <button 
          className={`${styles.managersTab} ${activeTab === 'current' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('current')}
        >
          Current
        </button>
        <button 
          className={`${styles.managersTab} ${activeTab === 'past' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('past')}
        >
          Past
        </button>
      </div>
      
      <div className={styles.buttonContainer}>
        <button 
          className={styles.actionButton}
          onClick={() => openSearchModal(activeTab === 'past')}
        >
          <FontAwesomeIcon icon={faPlus} />
          Add {activeTab === 'past' ? 'Past ' : ''}Manager
        </button>
      </div>

      {/* Display current managers */}
      {activeTab === 'current' && (
        <div className={styles.managerGridContainer}>
          {displayedCurrentManagers.length > 0 ? (
            <>
              <div className={styles.managerGrid}>
                {displayedCurrentManagers.map((manager, index) => (
                  <div 
                    key={manager.id || `current-${manager.userId}-${index}`} 
                    className={`${styles.managerCard} ${manager.isPending ? styles.pendingCard : ''}`}
                  >
                    <div className={styles.cardContent}>
                      <img 
                        src={manager.profilePictureUrl || defaultProfilePic} 
                        alt={manager.fullName}
                        className={styles.managerPhoto}
                        onClick={() => navigateToManagerProfile(manager.userName)}
                      />
                      <div className={styles.managerInfo}>
                        <h3 onClick={() => navigateToManagerProfile(manager.userName)}>{manager.fullName}</h3>
                        <p className={styles.username}>@{manager.userName}</p>
                        <div className={styles.managerType}>
                          {manager.userSubType ? (
                            <span className={styles.managerSubTypeBadge}>
                              {manager.userSubType}
                            </span>
                          ) : (
                            <span className={styles.managerTypeBadge}>
                              <FontAwesomeIcon icon={faUserTie} /> Manager
                            </span>
                          )}
                        </div>
                        {manager.isPending && (
                          <div className={styles.pendingBadge}>
                            <FontAwesomeIcon icon={faClock} /> Pending Approval
                          </div>
                        )}
                        {manager.since && (
                          <div className={styles.sinceBadge}>
                            <FontAwesomeIcon icon={faCalendarAlt} /> {formatSince(manager.since)}
                          </div>
                        )}
                      </div>
                      <div className={styles.cardActions}>
                        <button 
                          className={styles.removeButton}
                          onClick={() => handleRemoveManager(manager, false)}
                          title={manager.isPending ? "Cancel Request" : "Remove"}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Load more button for current managers */}
              {hasMoreCurrent && (
                <button 
                  className={styles.loadMoreButton}
                  onClick={() => loadMoreManagers(false)}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <FontAwesomeIcon icon={faUserTie} className={styles.emptyIcon} />
              <p>No managers added yet. Click "Add Manager" to get started.</p>
            </div>
          )}
        </div>
      )}

      {/* Display past managers */}
      {activeTab === 'past' && (
        <div className={styles.managerGridContainer}>
          {displayedPastManagers.length > 0 ? (
            <>
              <div className={styles.managerGrid}>
                {displayedPastManagers.map((manager, index) => (
                  <div 
                    key={manager.id || `past-${manager.userId}-${index}`} 
                    className={`${styles.managerCard} ${manager.isPending ? styles.pendingCard : ''}`}
                  >
                    <div className={styles.cardContent}>
                      <img 
                        src={manager.profilePictureUrl || defaultProfilePic} 
                        alt={manager.fullName}
                        className={styles.managerPhoto}
                        onClick={() => navigateToManagerProfile(manager.userName)}
                      />
                      <div className={styles.managerInfo}>
                        <h3 onClick={() => navigateToManagerProfile(manager.userName)}>{manager.fullName}</h3>
                        <p className={styles.username}>@{manager.userName}</p>
                        <div className={styles.managerType}>
                          {manager.userSubType ? (
                            <span className={styles.managerSubTypeBadge}>
                              {manager.userSubType}
                            </span>
                          ) : (
                            <span className={styles.managerTypeBadge}>
                              <FontAwesomeIcon icon={faUserTie} /> Manager
                            </span>
                          )}
                        </div>
                        {manager.isPending && (
                          <div className={styles.pendingBadge}>
                            <FontAwesomeIcon icon={faClock} /> Pending Approval
                          </div>
                        )}
                        {manager.duration && (
                          <div className={styles.durationBadge}>
                            <FontAwesomeIcon icon={faCalendarAlt} />
                            <span>{formatDuration(manager.duration)}</span>
                          </div>
                        )}
                      </div>
                      <div className={styles.cardActions}>
                        <button 
                          className={styles.removeButton}
                          onClick={() => handleRemoveManager(manager, true)}
                          title={manager.isPending ? "Cancel Request" : "Remove"}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Load more button for past managers */}
              {hasMorePast && (
                <button 
                  className={styles.loadMoreButton}
                  onClick={() => loadMoreManagers(true)}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <FontAwesomeIcon icon={faUserTie} className={styles.emptyIcon} />
              <p>No past managers added yet. Click "Add Past Manager" to get started.</p>
            </div>
          )}
        </div>
      )}

      {/* Search Modal with date fields */}
      {showSearchModal && (
        <SearchManagersModal
          isOpen={showSearchModal}
          onClose={() => {
            setShowSearchModal(false);
            setIsPastSearch(false);
          }}
          onSelect={handleSearchSelection}
          isPastSearch={isPastSearch}
          userData={userData}
        />
      )}
      
      {/* Loading indicator */}
      {isLoading && (
        <div className={styles.loadingState}>
          <div className={styles.spinner}></div>
        </div>
      )}
    </div>
  );
};

export default ManagedBySection;